import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ExpertSearchRow from './searchExpertsComponents/ExpertSearchRow';
import { ROUTES } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { followApi } from '../services/followService';
import { TopExpert, tipsterApi } from '../services/tipsterService';
import { TipsterPublic } from '../types/interfaces';
import '../styles/sections.scss';

type ExpertsRange = 'all' | '30d';

interface TopExpertsSectionProps {
  limit?: number;
  showRangeSwitch?: boolean;
}

const toTipsterPublic = (expert: TopExpert): TipsterPublic => ({
  id: expert.id,
  full_name: expert.full_name,
  country: expert.country,
  appreciation: 0,
  description: null,
  is_verified: expert.is_verified,
  tag_1: expert.tag_1,
  tag_2: expert.tag_2,
  tag_3: expert.tag_3,
  avatar_config: expert.avatar_config ?? null,
  avatar_matrix: expert.avatar_matrix ?? null,
  followers_count: expert.followers_count,
  recommendations_count: expert.total_picks,
  roi: expert.roi,
  last_picks: expert.last_picks ?? [],
});

const TopExpertsSection: React.FC<TopExpertsSectionProps> = ({ limit = 10, showRangeSwitch = false }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [experts, setExperts] = useState<TopExpert[]>([]);
  const [followedIds, setFollowedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<ExpertsRange>(showRangeSwitch ? '30d' : 'all');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      tipsterApi.getLeaderboard(limit, range),
      followApi.getFollowedIds().catch(() => [] as number[]),
    ])
      .then(([expertsData, ids]) => {
        setExperts(expertsData);
        setFollowedIds(new Set(ids));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [limit, range]);

  const handleToggleFollow = async (tipsterId: number, currentlyFollowing: boolean) => {
    if (!isAuthenticated) {
      navigate(`${ROUTES.LOGIN}?redirect=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    try {
      if (currentlyFollowing) {
        await followApi.unfollow(tipsterId);
        setFollowedIds(prev => {
          const next = new Set(prev);
          next.delete(tipsterId);
          return next;
        });
        setExperts(prev =>
          prev.map(e => e.id === tipsterId ? { ...e, followers_count: e.followers_count - 1 } : e)
        );
      } else {
        await followApi.follow(tipsterId);
        setFollowedIds(prev => new Set(prev).add(tipsterId));
        setExperts(prev =>
          prev.map(e => e.id === tipsterId ? { ...e, followers_count: e.followers_count + 1 } : e)
        );
      }
    } catch {
      // silently fail
    }
  };

  return (
    <div className="top-section">
      <div className="top-section__header">
        <h2 className="top-section__title">{t.hub.topExperts}</h2>
        <span className="top-section__subtitle">{t.hub.rankedByRoi}</span>
        {showRangeSwitch && (
          <div className="top-section__range-switch">
            <button
              type="button"
              className={`top-section__range-btn ${range === '30d' ? 'top-section__range-btn--active' : ''}`}
              onClick={() => setRange('30d')}
            >
              {t.hub.last30Days}
            </button>
            <button
              type="button"
              className={`top-section__range-btn ${range === 'all' ? 'top-section__range-btn--active' : ''}`}
              onClick={() => setRange('all')}
            >
              {t.hub.ever}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="top-section__loading">{t.common.loading}</div>
      ) : experts.length === 0 ? (
        <div className="top-section__empty">{t.hub.noStatsYet}</div>
      ) : (
        <ol className="top-experts__list">
          {experts.map((expert, index) => (
            <li key={expert.id}>
              <ExpertSearchRow
                tipster={toTipsterPublic(expert)}
                isFollowing={followedIds.has(expert.id)}
                onToggleFollow={handleToggleFollow}
                topPlace={index + 1}
                rankVariant="medal"
                showRoi
              />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default TopExpertsSection;
